/**
 * Year + age labelling — the single source of truth.
 *
 * Before this module the app rendered "which year is this and how old is
 * everyone" in nine different shapes ("2052 (Age 81/79)", "Year 27 (2052) —
 * Age 81/79", "2052 · Age 81", "2052, age 81", ...). Every one of them made a
 * reader re-learn the pattern on the next screen.
 *
 * Three variants, because the space budgets genuinely differ — an axis tick and
 * a modal heading cannot carry the same string — but nothing beyond three:
 *
 *   compact   axis ticks.
 *             Stacked lines, calendar year leading. Ages are separate lines,
 *             never joined, so no reader has to work out who "81/79" means.
 *
 *   inline    chart tooltips, detail-panel headers, wizard field hints.
 *             Calendar year leads: a chart's X axis *is* a calendar timeline,
 *             so leading with the internal "Year 27" index adds a translation
 *             step for no benefit.
 *
 *   heading   modal <h2>, card <h4>.
 *             Plan year leads — headings sit on surfaces the user navigates by
 *             plan year (the year-nav tabs, the wizard's phase rows). Ages are
 *             deliberately NOT part of the string; render them as a sibling
 *             subtitle via formatAgeSegments so the heading stays scannable.
 *
 * Separators, one system only:
 *   (...)  pairs the two names for the same year (calendar <-> plan)
 *   ` · `  divides categories of information (year info vs age info)
 *   `, `   joins repeated items of one category (person, person)
 * The em dash is deliberately absent — it was a fourth separator to learn.
 *
 * Ranges ("Year 1–5", "Paul 55–60") are NOT handled here. A span the user
 * defined is a different unit from a point-in-time label, and
 * `spendingPhaseHelpers.ts` already implements it correctly.
 */

/** One person's age at a point in time. `name` may be a full name; only the first word is shown. */
export interface PersonAge {
  name: string;
  age: number;
}

export type YearAgeVariant = 'compact' | 'inline' | 'heading';

export interface YearAgeLabelInput {
  planYear: number;
  calendarYear: number;
  /**
   * People to name. Callers should pass everyone; the deceased are filtered
   * here so no call site has to remember to. Omit entirely to render the year
   * alone.
   */
  people?: readonly PersonAgeInput[];
}

/** A person as the projection returns them, before filtering. */
export interface PersonAgeInput extends PersonAge {
  /** Absent is treated as alive — legacy projections predate the flag. */
  isAlive?: boolean;
}

/** "Paul Freeman" -> "PF". Blank-safe. */
export function getInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter((w) => w.length > 0);
  if (words.length === 0) return '?';
  return words.map((w) => w[0]).join('').toUpperCase();
}

/** "Paul Freeman" -> "Paul". Blank-safe. */
export function firstName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '';
  return trimmed.split(/\s+/)[0];
}

/**
 * Anyone the plan has already buried is dropped, rather than having their age
 * tick upward forever. Charts used to keep incrementing past life expectancy,
 * which in a tool whose whole job is modelling death read as a bug.
 */
export function alivePeople(people: readonly PersonAgeInput[]): PersonAge[] {
  return people
    .filter((p) => p.isAlive !== false)
    .map(({ name, age }) => ({ name, age }));
}

/**
 * "Paul 81, Kathryn 79", or "Age 81" when only one person remains.
 *
 * The name is dropped in the solo case because it is genuinely redundant — a
 * single-person plan has no one to distinguish them from — and "Age" carries
 * the meaning the name would otherwise imply. Returns '' for nobody, so
 * callers can render the year alone without a dangling separator.
 */
export function formatAgeSegments(people: readonly PersonAge[]): string {
  if (people.length === 0) return '';
  if (people.length === 1) return `Age ${people[0].age}`;
  // A name can legitimately be absent: Monte Carlo's yearlyStats carries only a
  // joined "81/79" string, so those people have no name to show. Without this
  // guard the segment renders as " 81" and the join yields " 81,  79".
  return people.map((p) => (p.name ? `${firstName(p.name)} ${p.age}` : String(p.age))).join(', ');
}

/**
 * The stacked lines of the `compact` variant, outermost first.
 *
 * Initials, not first names — this renders under every tick of a 45-year axis,
 * where "Paul 66" collides with its neighbours and "PF 66" does not. The
 * ambiguity that rules initials out elsewhere doesn't bite here: each person is
 * on their own line in a fixed order, so nothing has to be decoded positionally.
 */
export function formatCompactLines(input: YearAgeLabelInput): string[] {
  const people = alivePeople(input.people ?? []);
  return [
    String(input.calendarYear),
    `Year ${input.planYear}`,
    ...people.map((p) => `${p.name ? `${getInitials(p.name)} ` : ''}${p.age}`),
  ];
}

/**
 * The one entry point. `compact` returns its lines newline-joined; use
 * `formatCompactLines` directly where the lines are rendered as separate
 * elements (SVG <text>, stacked spans).
 */
export function formatYearAge(input: YearAgeLabelInput, variant: YearAgeVariant): string {
  const { planYear, calendarYear } = input;

  if (variant === 'compact') return formatCompactLines(input).join('\n');

  // Ages never enter a heading string — they belong in a sibling element.
  if (variant === 'heading') return `Year ${planYear} (${calendarYear})`;

  const ages = formatAgeSegments(alivePeople(input.people ?? []));
  const year = `${calendarYear} (Year ${planYear})`;
  return ages ? `${year} · ${ages}` : year;
}

/**
 * Adapts the projection's `persons` shape to this module's input. Every caller
 * reading a `HouseholdYearProjection` should come through here rather than
 * mapping the fields inline, so the name/age/alive mapping lives in one place.
 */
export function toPeopleForLabel(
  persons: readonly { personName: string; age: number; isAlive?: boolean }[],
): PersonAgeInput[] {
  return persons.map((p) => ({ name: p.personName, age: p.age, isAlive: p.isAlive }));
}

/** The projection-year shape every chart and panel already has to hand. */
export interface ProjectionYearLike {
  year: number;
  calendarYear: number;
  persons: readonly { personName: string; age: number; isAlive?: boolean }[];
}

/**
 * Label a year straight from a projection year — the common case, and the one
 * that was being spelled out identically at every slider and panel.
 */
export function formatProjectionYear(
  projectionYear: ProjectionYearLike,
  variant: YearAgeVariant,
): string {
  return formatYearAge(
    {
      planYear: projectionYear.year,
      calendarYear: projectionYear.calendarYear,
      people: toPeopleForLabel(projectionYear.persons),
    },
    variant,
  );
}
